import { useState, useEffect, useCallback } from 'react';
import {
  adminGetPosts,
  adminGetPostComments,
  adminDeletePost,
  adminDeleteComment,
  adminFlagPost,
  adminConfirmPost,
  adminGetAds,
  adminCreateAd,
  adminToggleAd,
  adminDeleteAd,
  adminReviewAdRequest,
  adminSettleAdPayment,
} from '../services/api';
import { getApiBase } from '../services/runtimeConfig';

const API_BASE = getApiBase();

function blockMediaActions(event) {
  event.preventDefault();
}

function PostRow({ post, onDelete, onFlag, onConfirm }) {
  const [comments, setComments] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);

  async function toggleComments() {
    if (!showComments && comments === null) {
      setLoadingComments(true);
      try {
        const data = await adminGetPostComments(post._id);
        setComments(data);
      } catch {
        setComments([]);
      } finally {
        setLoadingComments(false);
      }
    }
    setShowComments((v) => !v);
  }

  async function handleDeleteComment(commentId) {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await adminDeleteComment(commentId);
      setComments((prev) => prev.filter((c) => c._id !== commentId));
    } catch (err) {
      alert(err.message);
    }
  }

  const mediaFull = (url) => `${API_BASE}${url}`;
  const reportReasons = Array.isArray(post.reportReasons) ? post.reportReasons : [];
  const reportSummary = reportReasons.reduce((acc, reason) => {
    acc[reason] = (acc[reason] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className={`admin-post-row${post.flagged ? ' admin-post-flagged' : ''}${post.confirmed ? ' admin-post-confirmed' : ''}`}>
      <div className="admin-post-meta">
        <span className="post-username">{post.username}</span>
        <span className="admin-post-time">
          {new Date(post.timestamp).toLocaleString()}
        </span>
        {post.flagged && <span className="flag-badge">⚑ Flagged</span>}
        {post.confirmed && <span className="ad-status-badge active">Confirmed</span>}
      </div>

      <p className="admin-post-content">{post.content}</p>

      {(post.reportCount > 0 || post.confirmed) && (
        <div className="admin-post-report-box">
          <div className="admin-post-report-title">
            Reports: <strong>{post.reportCount || 0}</strong>
          </div>
          {Object.keys(reportSummary).length > 0 && (
            <div className="admin-post-report-reasons">
              {Object.entries(reportSummary).map(([reason, count]) => (
                <span key={reason} className="location-badge">
                  {reason.replace(/_/g, ' ')} ({count})
                </span>
              ))}
            </div>
          )}
          {post.confirmed && (
            <p className="admin-post-time">
              Confirmed by {post.confirmedBy || 'admin'}
              {post.confirmedAt ? ` on ${new Date(post.confirmedAt).toLocaleString()}` : ''}
            </p>
          )}
        </div>
      )}

      {post.media && post.media.length > 0 && (
        <div className="admin-media-row">
          {post.media.map((url, i) => {
            const isVideo = /\.(mp4|webm|mov)$/i.test(url);
            return isVideo ? (
              <video
                key={i}
                className="admin-media-thumb"
                src={mediaFull(url)}
                controls
                controlsList="nodownload noremoteplayback"
                disablePictureInPicture
                disableRemotePlayback
                onContextMenu={blockMediaActions}
              />
            ) : (
              <img
                key={i}
                className="admin-media-thumb"
                src={mediaFull(url)}
                alt="attachment"
                draggable={false}
                onDragStart={blockMediaActions}
                onContextMenu={blockMediaActions}
              />
            );
          })}
        </div>
      )}

      <div className="admin-post-actions">
        <button className="btn-ghost" onClick={toggleComments}>
          {showComments ? 'Hide' : 'Comments'}
          {comments !== null ? ` (${comments.length})` : ''}
        </button>
        <button
          className={`btn-ghost ${post.flagged ? 'btn-warning' : 'btn-flag'}`}
          onClick={() => onFlag(post._id)}
        >
          {post.flagged ? '⚑ Unflag' : '⚑ Flag'}
        </button>
        <button
          className={`btn-ghost ${post.confirmed ? 'tab-active' : ''}`}
          onClick={() => onConfirm(post._id)}
        >
          {post.confirmed ? 'Verified ✓' : 'Confirm Post'}
        </button>
        <button className="btn-danger" onClick={() => onDelete(post._id)}>
          🗑 Delete Post
        </button>
      </div>

      {showComments && (
        <div className="admin-comments">
          {loadingComments && <p className="loading-text">Loading…</p>}
          {!loadingComments && comments && comments.length === 0 && (
            <p className="empty-text">No comments.</p>
          )}
          {comments &&
            comments.map((c) => (
              <div key={c._id} className="admin-comment-row">
                <div className="admin-comment-meta">
                  <span className="post-username">{c.username}</span>
                  <span className="admin-post-time">
                    {new Date(c.timestamp).toLocaleString()}
                  </span>
                </div>
                <p className="admin-comment-content">{c.content}</p>
                <button
                  className="btn-danger btn-sm"
                  onClick={() => handleDeleteComment(c._id)}
                >
                  Delete
                </button>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState('posts'); // 'posts' | 'ads'
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadPosts = useCallback(async (p) => {
    setLoading(true);
    setError('');
    try {
      const data = await adminGetPosts(p);
      setPosts(data.posts);
      setTotalPages(data.totalPages);
      setTotal(data.total);
    } catch (err) {
      if (err.message === 'Invalid token' || err.message === 'Token expired') {
        localStorage.removeItem('admin_token');
        onLogout && onLogout();
        return;
      }
      setError(err.message || 'Failed to load posts.');
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    loadPosts(page);
  }, [page, loadPosts]);

  async function handleDelete(id) {
    if (!window.confirm('Permanently delete this post and all its comments?')) return;
    try {
      await adminDeletePost(id);
      setPosts((prev) => prev.filter((p) => p._id !== id));
      setTotal((t) => t - 1);
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleFlag(id) {
    try {
      const result = await adminFlagPost(id);
      setPosts((prev) =>
        prev.map((p) => (p._id === id ? { ...p, flagged: result.flagged } : p))
      );
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleConfirm(id) {
    try {
      const result = await adminConfirmPost(id);
      setPosts((prev) =>
        prev.map((p) => (
          p._id === id
            ? {
                ...p,
                confirmed: result.confirmed,
                confirmedAt: result.confirmedAt,
                confirmedBy: result.confirmedBy,
              }
            : p
        ))
      );
    } catch (err) {
      alert(err.message);
    }
  }

  function handleLogout() {
    localStorage.removeItem('admin_token');
    onLogout && onLogout();
  }

  // ─── Ads state ────────────────────────────────────────────────────────────────
  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adsError, setAdsError] = useState('');
  const [adForm, setAdForm] = useState({ businessName: '', tagline: '', imageUrl: '', linkUrl: '' });
  const [adSubmitting, setAdSubmitting] = useState(false);
  const [adFormError, setAdFormError] = useState('');
  const [adStatusFilter, setAdStatusFilter] = useState('');

  const loadAds = useCallback(async () => {
    setAdsLoading(true);
    setAdsError('');
    try {
      const data = await adminGetAds(adStatusFilter);
      setAds(data);
    } catch (err) {
      setAdsError(err.message || 'Failed to load ads.');
    } finally {
      setAdsLoading(false);
    }
  }, [adStatusFilter]);

  useEffect(() => {
    if (tab === 'ads') loadAds();
  }, [tab, loadAds]);

  async function handleAdReview(ad, action) {
    if (!window.confirm(`${action === 'approve' ? 'Approve' : 'Reject'} this ad request?`)) return;

    let payload = { action };

    if (action === 'approve') {
      const linkUrl = window.prompt('Destination link (https://...)', ad.linkUrl && ad.linkUrl !== '#' ? ad.linkUrl : 'https://');
      if (!linkUrl) return;
      const tagline = window.prompt('Short tagline', ad.tagline || ad.description?.slice(0, 120) || ad.businessName);
      const quoteAmount = window.prompt('Quote amount (numbers only)', ad.quoteAmount || '');
      if (!quoteAmount) return;
      const quoteCurrency = window.prompt('Quote currency code', ad.quoteCurrency || 'USD');
      const paymentLink = window.prompt('Payment link (https://...)', ad.paymentLink || 'https://');
      if (!paymentLink) return;
      payload = {
        ...payload,
        linkUrl,
        tagline: tagline || ad.tagline,
        adType: ad.adType || 'banner',
        keywords: Array.isArray(ad.keywords) ? ad.keywords : [],
        quoteAmount,
        quoteCurrency: quoteCurrency || 'USD',
        paymentLink,
      };
    } else {
      const reviewNote = window.prompt('Optional rejection reason', 'Not a fit right now');
      payload.reviewNote = reviewNote || 'Rejected by admin';
    }

    try {
      const updated = await adminReviewAdRequest(ad._id, payload);
      setAds((prev) => prev.map((item) => (item._id === ad._id ? updated : item)));
      if (adStatusFilter && updated.status !== adStatusFilter) {
        setAds((prev) => prev.filter((item) => item._id !== ad._id));
      }
    } catch (err) {
      alert(err.message || 'Failed to review ad request');
    }
  }

  async function handleAdSettle(id, settled) {
    try {
      const updated = await adminSettleAdPayment(id, settled);
      setAds((prev) => prev.map((a) => (a._id === id ? updated : a)));
    } catch (err) {
      alert(err.message || 'Failed to update payment status');
    }
  }

  async function handleAdCreate(e) {
    e.preventDefault();
    setAdFormError('');
    setAdSubmitting(true);
    try {
      const created = await adminCreateAd(adForm);
      setAds((prev) => [created, ...prev]);
      setAdForm({ businessName: '', tagline: '', imageUrl: '', linkUrl: '' });
    } catch (err) {
      setAdFormError(err.message);
    } finally {
      setAdSubmitting(false);
    }
  }

  async function handleAdToggle(id) {
    try {
      const result = await adminToggleAd(id);
      setAds((prev) => prev.map((a) => (a._id === id ? { ...a, active: result.active } : a)));
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleAdDelete(id) {
    if (!window.confirm('Delete this ad permanently?')) return;
    try {
      await adminDeleteAd(id);
      setAds((prev) => prev.filter((a) => a._id !== id));
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div>
          <h1>Moderation Dashboard</h1>
          <span className="admin-count">{total} total posts</span>
        </div>
        <div className="admin-header-actions">
          <button
            className={`btn-ghost${tab === 'posts' ? ' tab-active' : ''}`}
            onClick={() => setTab('posts')}
          >
            Posts
          </button>
          <button
            className={`btn-ghost${tab === 'ads' ? ' tab-active' : ''}`}
            onClick={() => setTab('ads')}
          >
            Ads
          </button>
          <button className="btn-ghost" onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </header>

      {tab === 'posts' && (
        <>
          {error && (
            <div className="error-banner">
              {error}{' '}
              <button className="btn-ghost" onClick={() => loadPosts(page)}>
                Retry
              </button>
            </div>
          )}

          {loading && <div className="spinner">Loading…</div>}

          {!loading && posts.length === 0 && !error && (
            <div className="empty-state">No posts found.</div>
          )}

          <div className="admin-post-list">
            {posts.map((post) => (
              <PostRow
                key={post._id}
                post={post}
                onDelete={handleDelete}
                onFlag={handleFlag}
                onConfirm={handleConfirm}
              />
            ))}
          </div>
        </>
      )}

      {tab === 'posts' && totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn-ghost"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            ← Prev
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            className="btn-ghost"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
          >
            Next →
          </button>
        </div>
      )}

      {/* ─── Ads Tab ─────────────────────────────────────────────────────────── */}
      {tab === 'ads' && (
        <div className="ads-panel">
          <h2 className="ads-panel-title">Advertisements</h2>

          <div className="ad-filter-row">
            <button className={`btn-ghost btn-sm${adStatusFilter === '' ? ' tab-active' : ''}`} onClick={() => setAdStatusFilter('')}>All</button>
            <button className={`btn-ghost btn-sm${adStatusFilter === 'pending' ? ' tab-active' : ''}`} onClick={() => setAdStatusFilter('pending')}>Pending</button>
            <button className={`btn-ghost btn-sm${adStatusFilter === 'approved' ? ' tab-active' : ''}`} onClick={() => setAdStatusFilter('approved')}>Approved</button>
            <button className={`btn-ghost btn-sm${adStatusFilter === 'rejected' ? ' tab-active' : ''}`} onClick={() => setAdStatusFilter('rejected')}>Rejected</button>
          </div>

          {/* Create form */}
          <form className="ad-create-form" onSubmit={handleAdCreate}>
            <h3>Add New Ad</h3>
            <input
              className="ad-input"
              placeholder="Business name *"
              value={adForm.businessName}
              onChange={(e) => setAdForm((f) => ({ ...f, businessName: e.target.value }))}
              required
              maxLength={200}
            />
            <input
              className="ad-input"
              placeholder="Tagline *"
              value={adForm.tagline}
              onChange={(e) => setAdForm((f) => ({ ...f, tagline: e.target.value }))}
              required
              maxLength={300}
            />
            <input
              className="ad-input"
              placeholder="Image URL (optional)"
              value={adForm.imageUrl}
              onChange={(e) => setAdForm((f) => ({ ...f, imageUrl: e.target.value }))}
              type="url"
            />
            <input
              className="ad-input"
              placeholder="Link URL * (https://...)"
              value={adForm.linkUrl}
              onChange={(e) => setAdForm((f) => ({ ...f, linkUrl: e.target.value }))}
              required
              type="url"
            />
            {adFormError && <p className="form-error">{adFormError}</p>}
            <button className="btn-primary" type="submit" disabled={adSubmitting}>
              {adSubmitting ? 'Creating…' : 'Create Ad'}
            </button>
          </form>

          {/* Ads list */}
          {adsLoading && <p className="loading-text">Loading ads…</p>}
          {adsError && <p className="form-error">{adsError}</p>}
          {!adsLoading && ads.length === 0 && (
            <p className="empty-text">No ads yet. Create one above.</p>
          )}
          <div className="ads-list">
            {ads.map((ad) => (
              <div key={ad._id} className={`ad-row${ad.active ? '' : ' ad-row-inactive'}`}>
                {ad.imageUrl && (
                  <img className="ad-row-thumb" src={ad.imageUrl} alt={ad.businessName} />
                )}
                <div className="ad-row-info">
                  <span className="ad-row-name">{ad.businessName}</span>
                  {ad.email && <span className="ad-row-tagline">Contact: {ad.email}</span>}
                  <span className="ad-row-tagline">{ad.tagline}</span>
                  <a className="ad-row-link" href={ad.linkUrl} target="_blank" rel="noopener noreferrer">
                    {ad.linkUrl}
                  </a>
                  {ad.quoteAmount && (
                    <span className="ad-row-tagline">Quote: {ad.quoteCurrency || 'USD'} {ad.quoteAmount}</span>
                  )}
                  {ad.paymentLink && (
                    <a className="ad-row-link" href={ad.paymentLink} target="_blank" rel="noopener noreferrer">
                      Payment Link
                    </a>
                  )}
                  {Array.isArray(ad.attachmentUrls) && ad.attachmentUrls.length > 0 && (
                    <div className="ad-attachments-list">
                      {ad.attachmentUrls.map((url, idx) => (
                        <a key={`${url}-${idx}`} className="ad-row-link" href={`${API_BASE}${url}`} target="_blank" rel="noopener noreferrer">
                          Attachment {idx + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="ad-row-actions">
                  <span className={`ad-status-badge${ad.status === 'approved' && ad.active ? ' active' : ' inactive'}`}>
                    {ad.status === 'pending'
                      ? 'Pending'
                      : ad.status === 'rejected'
                        ? 'Rejected'
                        : ad.paymentStatus === 'settled'
                          ? (ad.active ? 'Active' : 'Paused')
                          : 'Awaiting Payment'}
                  </span>
                  {ad.status === 'pending' && (
                    <>
                      <button
                        className="btn-primary btn-sm"
                        onClick={() => handleAdReview(ad, 'approve')}
                      >
                        Approve
                      </button>
                      <button
                        className="btn-warning btn-sm"
                        onClick={() => handleAdReview(ad, 'reject')}
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {ad.status === 'approved' && ad.paymentStatus !== 'settled' && (
                    <button
                      className="btn-primary btn-sm"
                      onClick={() => handleAdSettle(ad._id, true)}
                    >
                      Mark Settled & Activate
                    </button>
                  )}
                  {ad.status === 'approved' && ad.paymentStatus === 'settled' && (
                    <button
                      className="btn-warning btn-sm"
                      onClick={() => handleAdSettle(ad._id, false)}
                    >
                      Mark Unsettled
                    </button>
                  )}
                  <button
                    className="btn-ghost btn-sm"
                    onClick={() => handleAdToggle(ad._id)}
                    disabled={ad.status !== 'approved' || ad.paymentStatus !== 'settled'}
                  >
                    {ad.active ? 'Pause' : 'Activate'}
                  </button>
                  <button
                    className="btn-danger btn-sm"
                    onClick={() => handleAdDelete(ad._id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
